import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Coins, Zap, Crown, Sparkles, Wand2, Loader2, Mail, Lock, Eye, EyeOff, Check, User, Phone, MapPin, Shield, CreditCard } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface SubscriptionPlan {
  id: string;
  name: string;
  monthly_credits: number;
  price: number;
  features: string[];
  is_free: boolean;
}

const Signup = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const planId = searchParams.get('plan');
  
  const [plan, setPlan] = useState<SubscriptionPlan | null>(null);
  const [isLoadingPlan, setIsLoadingPlan] = useState(true);
  
  // Form state
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [authMode, setAuthMode] = useState<'signup' | 'signin'>('signup');
  // In signup mode the user must pick a method (email / Google / Facebook /
  // LinkedIn) before the email form is revealed.
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [isRedirectingToCheckout, setIsRedirectingToCheckout] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  const US_STATES = [
    "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
    "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
    "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
    "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
    "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY", "DC"
  ];

  useEffect(() => {
    if (planId) {
      loadPlan(planId);
    } else {
      // A plan must be selected before an account can be created.
      navigate('/pricing', { replace: true });
    }
  }, [planId]);

  useEffect(() => {
    if (user && plan) {
      if (plan.is_free) {
        toast.success("You're on the Free plan!");
        // Honor `?from=` so funnels that route through pricing (e.g. the
        // homepage free-resume-tester CTA) can land users on the page they
        // were trying to reach instead of the generic homepage.
        const from = searchParams.get('from');
        navigate(from && from.startsWith('/') ? from : '/');
      } else {
        handleCheckout();
      }
    }
  }, [user, plan]);

  const loadPlan = async (id: string) => {
    try {
      const { data, error } = await supabase
        .from('subscription_plans')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;

      setPlan({
        id: data.id,
        name: data.name,
        monthly_credits: data.monthly_credits,
        price: Math.round((data.price_cents || 0) / 100),
        features: Array.isArray(data.features) ? data.features as string[] : [],
        is_free: data.is_free
      });
    } catch (error) {
      console.error('Error loading plan:', error);
      toast.error('Failed to load plan details');
    } finally {
      setIsLoadingPlan(false);
    }
  };

  const handleCheckout = async () => {
    if (!plan || plan.is_free) return;
    // Auto-activation: create the subscription for this signed-in user and
    // send them to checkout. Payment confirms -> instant automatic activation.
    setIsRedirectingToCheckout(true);
    setCheckoutError(null);
    try {
      const { data, error } = await supabase.functions.invoke('square-checkout', {
        body: { plan_id: plan.id },
      });
      if (error || !data?.success || !data?.checkout_url) {
        throw new Error(data?.error || error?.message || 'Failed to start checkout');
      }
      window.location.href = data.checkout_url;
    } catch (error: any) {
      setIsRedirectingToCheckout(false);
      setCheckoutError(error.message || 'Failed to start checkout. Please try again.');
      toast.error(error.message || 'Failed to start checkout');
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!firstName || !lastName) {
      toast.error("Please enter your first and last name");
      return;
    }
    if (!city || !state) {
      toast.error("Please enter your city and state");
      return;
    }
    if (!email || !password) {
      toast.error("Please enter email and password");
      return;
    }
    if (password !== confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setIsSubmitting(true);

    try {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/pricing`,
          data: {
            first_name: firstName,
            last_name: lastName,
            city: city,
            state: state,
            phone: phone
          }
        }
      });

      if (error) {
        if (error.message.includes('already registered')) {
          toast.error("This email is already registered. Please sign in instead.");
          setAuthMode('signin');
        } else {
          throw error;
        }
      } else {
        toast.success("Account created! Proceeding to checkout...");
      }
    } catch (error: any) {
      console.error('Signup error:', error);
      toast.error(error.message || 'Failed to create account');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error("Please enter email and password");
      return;
    }

    setIsSubmitting(true);

    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });

      if (error) throw error;

      toast.success("Signed in! Proceeding to checkout...");
    } catch (error: any) {
      console.error('Signin error:', error);
      toast.error(error.message || 'Failed to sign in');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOAuth = async (
    provider: "google" | "facebook" | "linkedin_oidc",
  ) => {
    setIsSubmitting(true);
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: `${window.location.origin}/`,
          scopes:
            provider === "facebook" ? "email public_profile" : "openid profile email",
        },
      });
      if (error) {
        toast.error(error.message || `Failed to continue with ${provider}`);
        setIsSubmitting(false);
      }
      // On success the browser redirects to the provider — no need to reset state.
    } catch (e: any) {
      console.error(`${provider} OAuth error:`, e);
      toast.error(e.message || `Failed to continue with ${provider}`);
      setIsSubmitting(false);
    }
  };

  const getPlanIcon = (planName: string) => {
    switch (planName?.toLowerCase()) {
      case 'free': return Coins;
      case 'starter': return Zap;
      case 'pro': return Crown;
      case 'unlimited': return Sparkles;
      default: return Coins;
    }
  };

  const getPlanColor = () => {
    return 'from-primary to-primary-hover';
  };

  const Icon = plan ? getPlanIcon(plan.name) : Coins;

  const socialButtons = (
    <div className="space-y-2.5">
      <button
        type="button"
        onClick={() => handleOAuth('google')}
        disabled={isSubmitting}
        className="w-full h-11 rounded-lg border border-slate-300 bg-white text-[14px] font-semibold text-slate-700 hover:bg-slate-50 hover:border-slate-400 transition-colors flex items-center justify-center gap-2.5 disabled:opacity-60"
      >
        <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
        </svg>
        Continue with Google
      </button>
      <button
        type="button"
        onClick={() => handleOAuth('facebook')}
        disabled={isSubmitting}
        className="w-full h-11 rounded-lg border border-slate-300 bg-white text-[14px] font-semibold text-slate-700 hover:bg-slate-50 hover:border-slate-400 transition-colors flex items-center justify-center gap-2.5 disabled:opacity-60"
      >
        <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="#1877F2">
          <path d="M24 12.07C24 5.4 18.63 0 12 0S0 5.4 0 12.07c0 6.03 4.39 11.03 10.13 11.93v-8.44H7.08v-3.49h3.05V9.41c0-3.02 1.79-4.69 4.53-4.69 1.31 0 2.69.24 2.69.24v2.97h-1.51c-1.49 0-1.96.93-1.96 1.89v2.26h3.33l-.53 3.49h-2.8V24C19.61 23.1 24 18.1 24 12.07z" />
        </svg>
        Continue with Facebook
      </button>
      <button
        type="button"
        onClick={() => handleOAuth('linkedin_oidc')}
        disabled={isSubmitting}
        className="w-full h-11 rounded-lg border border-slate-300 bg-white text-[14px] font-semibold text-slate-700 hover:bg-slate-50 hover:border-slate-400 transition-colors flex items-center justify-center gap-2.5 disabled:opacity-60"
      >
        <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="#0A66C2">
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
        </svg>
        Continue with LinkedIn
      </button>
    </div>
  );

  if (isLoadingPlan) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-[#0B1C3F] via-[#0E2350] to-[#0B1C3F]">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 mx-auto rounded-full bg-primary/10 flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
          <p className="text-muted-foreground">Loading plan details...</p>
        </div>
      </div>
    );
  }

  if (isRedirectingToCheckout) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-b from-[#0B1C3F] via-[#0E2350] to-[#0B1C3F]">
        <div className="text-center space-y-6 max-w-md mx-auto px-4">
          <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center">
            <CreditCard className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-3xl font-bold text-foreground">Redirecting to Checkout</h2>
          <p className="text-muted-foreground text-lg">Please wait while we set up your secure payment...</p>
          <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
            <Shield className="w-4 h-4 text-success" />
            <span>256-bit SSL Encrypted</span>
          </div>
        </div>
      </div>
    );
  }

  if (checkoutError && user && plan && !plan.is_free) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-b from-[#0B1C3F] via-[#0E2350] to-[#0B1C3F] px-4">
        <div className="max-w-md w-full bg-card rounded-2xl shadow-xl border p-8 text-center space-y-6">
          <div className="w-16 h-16 mx-auto rounded-full bg-destructive/10 flex items-center justify-center">
            <CreditCard className="w-8 h-8 text-destructive" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-foreground mb-2">Checkout Error</h2>
            <p className="text-muted-foreground">{checkoutError}</p>
          </div>
          <div className="space-y-3">
            <Button onClick={handleCheckout} className="w-full" size="lg">
              Try Again
            </Button>
            <Button variant="outline" onClick={() => navigate('/pricing')} className="w-full">
              Back to Plans
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0B1C3F] via-[#0E2350] to-[#0B1C3F]">
      {/* Main Content */}
      <div className="min-h-screen flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-lg">
          {/* Header — centered, matches the sign-in page */}
          <div className="text-center mb-6">
            <button
              type="button"
              onClick={() => navigate('/pricing')}
              className="mb-5 inline-flex items-center gap-1.5 text-[13px] font-semibold text-slate-300 hover:text-[#C9A84C] transition-colors"
            >
              <ArrowLeft className="w-4 h-4" /> Back to Plans
            </button>
            <Link to="/" className="flex items-center justify-center gap-2">
              <span className="w-9 h-9 rounded-lg bg-[#0B1C3F] border border-[#C9A84C]/40 flex items-center justify-center">
                <Wand2 className="w-[18px] h-[18px] text-[#C9A84C]" />
              </span>
              <span className="text-2xl font-extrabold tracking-tight text-white">
                Tailor<span className="text-[#C9A84C]">Wiz</span>
              </span>
            </Link>
          </div>
          <div className="relative bg-white rounded-2xl shadow-2xl overflow-hidden border border-[#C9A84C]/20">
            {/* Form panel */}
            <div className="p-6 sm:p-8">
              <div className="max-w-md mx-auto">
                {/* Compact plan summary */}
                {plan && (
                  <div className="mb-6 flex items-center justify-between gap-3 rounded-xl bg-[#0B1C3F] text-white px-4 py-3">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <span className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-[18px] h-[18px] text-[#C9A84C]" />
                      </span>
                      <div className="min-w-0">
                        <div className="text-[13px] font-bold truncate">{plan.name} Plan</div>
                        <div className="text-[11px] text-slate-300">
                          {plan.monthly_credits >= 9999 ? "Unlimited" : plan.monthly_credits} credits/month
                        </div>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-[20px] font-extrabold text-[#C9A84C] leading-none">
                        {plan.is_free ? "Free" : `$${plan.price}`}
                      </div>
                      {!plan.is_free && (
                        <div className="text-[10px] text-slate-300 mt-0.5">/month</div>
                      )}
                    </div>
                  </div>
                )}

                {/* Form Header */}
                <div className="text-center mb-6">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-[#0B1C3F] mb-3">
                    <User className="w-6 h-6 text-[#C9A84C]" />
                  </div>
                  <h1 className="text-[24px] font-extrabold tracking-tight text-[#0B1C3F]">
                    {authMode === 'signup' ? 'Create Your Account' : 'Welcome Back'}
                  </h1>
                  <p className="text-[14px] text-slate-500 mt-1">
                    {authMode === 'signin'
                      ? 'Sign in to continue to checkout'
                      : showEmailForm
                        ? 'Enter your details to get started'
                        : 'Choose how you want to sign up'}
                  </p>
                </div>

                {authMode === 'signup' && !showEmailForm ? (
                  /* Method chooser — pick one of four ways to sign up */
                  <div className="space-y-3">
                    <button
                      type="button"
                      onClick={() => setShowEmailForm(true)}
                      className="w-full h-12 rounded-lg bg-[#C9A84C] text-[#0B1C3F] text-[15px] font-bold hover:bg-[#E5C36A] hover:shadow-lg hover:shadow-[#C9A84C]/30 transition-all flex items-center justify-center gap-2.5"
                    >
                      <Mail className="w-[18px] h-[18px]" />
                      Sign up with Email
                    </button>

                    <div className="relative py-1">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-slate-200"></div>
                      </div>
                      <div className="relative flex justify-center text-[11px] uppercase tracking-wide">
                        <span className="bg-white px-3 text-slate-400 font-semibold">or sign up with</span>
                      </div>
                    </div>

                    {socialButtons}

                    <button
                      type="button"
                      onClick={() => { setAuthMode('signin'); setShowEmailForm(true); }}
                      className="w-full text-[13px] font-semibold text-[#0B1C3F] hover:text-[#C9A84C] transition-colors pt-2"
                    >
                      Already have an account? Sign in
                    </button>
                  </div>
                ) : (
                <>
                {authMode === 'signup' && showEmailForm && (
                  <button
                    type="button"
                    onClick={() => setShowEmailForm(false)}
                    className="mb-4 inline-flex items-center gap-1.5 text-[13px] font-semibold text-slate-500 hover:text-[#0B1C3F] transition-colors"
                  >
                    <ArrowLeft className="w-4 h-4" /> Choose a different sign-up method
                  </button>
                )}
                <form onSubmit={authMode === 'signup' ? handleSignUp : handleSignIn} className="space-y-4">
                  {authMode === 'signup' && (
                    <>
                      {/* Name Row */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="firstName" className="text-sm font-medium">First Name</Label>
                          <div className="relative">
                            <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                              id="firstName"
                              type="text"
                              placeholder="John"
                              value={firstName}
                              onChange={(e) => setFirstName(e.target.value)}
                              className="pl-10 h-12 bg-muted/50 border-border focus:bg-background transition-colors"
                              required
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="lastName" className="text-sm font-medium">Last Name</Label>
                          <div className="relative">
                            <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                              id="lastName"
                              type="text"
                              placeholder="Doe"
                              value={lastName}
                              onChange={(e) => setLastName(e.target.value)}
                              className="pl-10 h-12 bg-muted/50 border-border focus:bg-background transition-colors"
                              required
                            />
                          </div>
                        </div>
                      </div>

                      {/* Location Row */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="city" className="text-sm font-medium">City</Label>
                          <div className="relative">
                            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                              id="city"
                              type="text"
                              placeholder="New York"
                              value={city}
                              onChange={(e) => setCity(e.target.value)}
                              className="pl-10 h-12 bg-muted/50 border-border focus:bg-background transition-colors"
                              required
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="state" className="text-sm font-medium">State</Label>
                          <Select value={state} onValueChange={setState}>
                            <SelectTrigger className="h-12 bg-muted/50 border-border focus:bg-background">
                              <SelectValue placeholder="Select" />
                            </SelectTrigger>
                            <SelectContent>
                              {US_STATES.map((st) => (
                                <SelectItem key={st} value={st}>{st}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      {/* Phone */}
                      <div className="space-y-2">
                        <Label htmlFor="phone" className="text-sm font-medium">Phone Number <span className="text-muted-foreground">(optional)</span></Label>
                        <div className="relative">
                          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input
                            id="phone"
                            type="tel"
                            placeholder="(555) 123-4567"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            className="pl-10 h-12 bg-muted/50 border-border focus:bg-background transition-colors"
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {/* Email */}
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-sm font-medium">Email Address</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="you@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="pl-10 h-12 bg-muted/50 border-border focus:bg-background transition-colors"
                        required
                      />
                    </div>
                  </div>

                  {/* Password */}
                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-sm font-medium">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="pl-10 pr-10 h-12 bg-muted/50 border-border focus:bg-background transition-colors"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>

                  {/* Confirm Password */}
                  {authMode === 'signup' && (
                    <div className="space-y-2">
                      <Label htmlFor="confirmPassword" className="text-sm font-medium">Confirm Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="confirmPassword"
                          type={showPassword ? "text" : "password"}
                          placeholder="••••••••"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          className="pl-10 h-12 bg-muted/50 border-border focus:bg-background transition-colors"
                          required
                        />
                      </div>
                    </div>
                  )}

                  {/* Submit Button */}
                  <div className="pt-1">
                    <Button
                      type="submit"
                      className="w-full h-12 text-base font-bold bg-[#C9A84C] text-[#0B1C3F] hover:bg-[#E5C36A] hover:shadow-lg hover:shadow-[#C9A84C]/30 transition-all duration-300"
                      size="lg"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          {authMode === 'signup' ? 'Creating Account...' : 'Signing In...'}
                        </>
                      ) : (
                        authMode === 'signup' ? 'Create Account & Continue' : 'Sign In & Continue'
                      )}
                    </Button>
                  </div>

                  {/* Social sign-in — sign-in mode only */}
                  {authMode === 'signin' && (
                    <>
                      <div className="relative py-4">
                        <div className="absolute inset-0 flex items-center">
                          <div className="w-full border-t border-slate-200"></div>
                        </div>
                        <div className="relative flex justify-center text-[11px] uppercase tracking-wide">
                          <span className="bg-white px-3 text-slate-400 font-semibold">
                            or sign in with
                          </span>
                        </div>
                      </div>
                      {socialButtons}
                    </>
                  )}

                  {/* Toggle Auth Mode */}
                  <button
                    type="button"
                    onClick={() => {
                      const next = authMode === 'signup' ? 'signin' : 'signup';
                      setAuthMode(next);
                      // Sign-in shows the form directly; sign-up returns to the chooser.
                      setShowEmailForm(next === 'signin');
                    }}
                    className="w-full text-[13px] font-semibold text-[#0B1C3F] hover:text-[#C9A84C] transition-colors pt-1"
                  >
                    {authMode === 'signup'
                      ? 'Already have an account? Sign in'
                      : "Don't have an account? Sign up"}
                  </button>
                </form>
                </>
                )}

                {/* Terms */}
                <div className="mt-8 pt-6 border-t border-border">
                  <p className="text-xs text-center text-muted-foreground">
                    By creating an account, you agree to our{' '}
                    <Link to="/terms" className="text-primary hover:underline font-medium">Terms of Service</Link>
                    {' '}and{' '}
                    <Link to="/privacy" className="text-primary hover:underline font-medium">Privacy Policy</Link>
                  </p>
                  <div className="flex items-center justify-center gap-4 mt-4 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1.5">
                      <Shield className="w-3.5 h-3.5 text-success" />
                      <span>SSL Secure</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Lock className="w-3.5 h-3.5 text-success" />
                      <span>Data Protected</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;
